import { lazy } from 'react';
import {
  type PatchRoutesOnNavigationFunction,
  RouterProvider,
  createBrowserRouter
} from 'react-router';

import { ADMIN_ROUTES } from './AdminPanel/shared/routes';
import AppProvider from './providers/app.provider';
import AuthorizedProvider from './providers/authorized.provider';
import Layout from './providers/layout/layout';
import ProtectedRoute from './providers/protected.route';
import SuspenseRoute from './providers/suspense-route/suspense.route';
import { ROUTES, WEBVIEW_ROUTES } from './shared/routes';
import { NotFound } from './shared/widgets';

const isAdminPath = (path: string): boolean =>
  path === ADMIN_ROUTES.root || path.startsWith(`${ADMIN_ROUTES.root}/`);

/**
 * Админка подгружается при первом заходе на /admin: её дерево маршрутов и код — в отдельном
 * чанке, пользователю оболочки не грузятся. Встраивается веткой верхнего уровня, вне
 * AppProvider (язык, тема, оверлей логина оболочки её не задевают).
 *
 * Почему не splat `/admin/*` с useRoutes внутри: splat проигрывает по рангу маршрутам
 * оболочки `/:lang/...` (`/admin/auth/login` уходил в `/:lang/auth/login`). Роутер зовёт эту
 * функцию, пока совпадение держится на динамическом сегменте (`/:lang` = `admin`), после
 * встраивания статические пути админки выигрывают честно.
 */
const patchAdminRoutes: PatchRoutesOnNavigationFunction = async ({
  path,
  patch
}) => {
  if (!isAdminPath(path)) return;
  const { adminRoute } = await import('./AdminPanel/admin-routes');
  // Повторный patch тем же деревом роутер дедуплицирует.
  patch(null, [adminRoute]);
};

const Home = lazy(() => import('./pages/Home/Home'));
const AutoCredit = lazy(() => import('./webview/AutoCredit/AutoCredit'));
const Leasing = lazy(() => import('./webview/Leasing/Leasing'));
const VehicleManagement = lazy(
  () => import('./webview/VehicleManagement/VehicleManagement')
);

const LoginPage = lazy(() => import('./pages/Auth/Login/Login'));
const RegistrationPage = lazy(
  () => import('./pages/Auth/Registration/Registration')
);
const ResetPassword = lazy(
  () => import('./pages/Auth/ResetPassword/ResetPassword')
);
const Welcome = lazy(() => import('./pages/Welcome/Welcome'));
const Landing = lazy(() => import('./pages/Landing/Landing'));

const Services = lazy(() => import('./pages/Services/Services'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const PreApprovalProvider = lazy(
  () => import('./pages/PreApproval/PreApprovalProvider')
);
const PreApproval = lazy(() => import('./pages/PreApproval/PreApproval'));
const PreApprovalApply = lazy(() => import('./pages/PreApproval/Apply/Apply'));
const PreApprovalCalculator = lazy(
  () => import('./pages/PreApproval/Calculator/Calculator')
);
const PreApprovalCars = lazy(() => import('./pages/PreApproval/Cars/Cars'));
const PreApprovalCarDetail = lazy(
  () => import('./pages/PreApproval/CarDetail/CarDetail')
);

const Notifications = lazy(() => import('./pages/Notifications/Notifications'));
const SupportContact = lazy(
  () => import('./pages/SupportContact/SupportContact')
);
const SupportRequests = lazy(
  () => import('./pages/SupportRequests/SupportRequests')
);
const SupportCreate = lazy(() => import('./pages/SupportCreate/SupportCreate'));
const SupportChat = lazy(() => import('./pages/SupportChat/SupportChat'));

const FAQ = lazy(() => import('./pages/FAQ/FAQ'));
const DeleteAccountInstructions = lazy(
  () => import('./pages/DeleteAccountInstructions/DeleteAccountInstructions')
);
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <AppProvider />,
      // Первый матч асинхронный (patchRoutesOnNavigation проверяет, не админка ли это) —
      // на это мгновение рисовать нечего: у оболочки и админки свои лоадеры дальше по дереву.
      HydrateFallback: () => null,
      children: [
        {
          path: '/:lang',
          element: <AuthorizedProvider />,
          children: [
            {
              element: <SuspenseRoute />,
              children: [
                {
                  path: ROUTES.welcome,
                  element: <Welcome />
                },
                {
                  path: ROUTES.login,
                  element: <LoginPage />
                },
                {
                  path: ROUTES.register,
                  element: <RegistrationPage />
                },
                {
                  path: ROUTES.resetPassword,
                  element: <ResetPassword />
                }
              ]
            },

            {
              element: <Layout />,
              children: [
                {
                  // Открыты и lazy-loaded.
                  element: <SuspenseRoute />,
                  children: [
                    {
                      path: ROUTES.home,
                      element: <Home />
                    },
                    {
                      // Splat — внутренние маршруты сервиса зеркалятся в URL.
                      path: `${WEBVIEW_ROUTES.autoCredit}/*`,
                      element: <AutoCredit />
                    },
                    {
                      path: ROUTES.services,
                      element: <Services />
                    },
                    {
                      path: ROUTES.profile,
                      element: <Profile />
                    },
                    {
                      path: ROUTES.supportContact,
                      element: <SupportContact />
                    }
                  ]
                },
                {
                  // Закрыты: требует авторизации сразу при входе.
                  element: <ProtectedRoute />,
                  children: [
                    {
                      // Закрыты и lazy-loaded.
                      element: <SuspenseRoute />,
                      children: [
                        {
                          path: `${WEBVIEW_ROUTES.leasing}/*`,
                          element: <Leasing />
                        },
                        {
                          path: `${WEBVIEW_ROUTES.vehicleManagement}/*`,
                          element: <VehicleManagement />
                        },
                        {
                          path: ROUTES.notifications,
                          element: <Notifications />
                        },
                        {
                          path: ROUTES.supportRequests,
                          element: <SupportRequests />
                        },
                        {
                          path: ROUTES.supportRequestCreate,
                          element: <SupportCreate />
                        },
                        {
                          // Раздел предодобрения: провайдер грузит процесс и ведёт на нужную страницу
                          element: <PreApprovalProvider />,
                          children: [
                            {
                              path: ROUTES.preApproval,
                              element: <PreApproval />
                            },
                            {
                              path: ROUTES.preApprovalApply,
                              element: <PreApprovalApply />
                            },
                            {
                              path: ROUTES.preApprovalCalculator,
                              element: <PreApprovalCalculator />
                            },
                            {
                              path: ROUTES.preApprovalCars,
                              element: <PreApprovalCars />
                            },
                            {
                              path: ROUTES.preApprovalCarDetail,
                              element: <PreApprovalCarDetail />
                            }
                          ]
                        }
                      ]
                    }
                  ]
                }
              ]
            },

            // Полноэкранные страницы (вне Layout: без общей шапки и таб-бара).
            {
              // Открыты и lazy-loaded.
              element: <SuspenseRoute />,
              children: [
                {
                  // Лендинг — корень языкового сегмента (/:lang).
                  index: true,
                  element: <Landing />
                },
                {
                  path: ROUTES.faq,
                  element: <FAQ />
                },
                {
                  path: ROUTES.deleteAccountInstructions,
                  element: <DeleteAccountInstructions />
                },
                {
                  path: ROUTES.privacyPolicy,
                  element: <PrivacyPolicy />
                }
              ]
            },
            {
              // Закрыты и без layout.
              element: <ProtectedRoute />,
              children: [
                {
                  // Закрыты + lazy-loaded.
                  element: <SuspenseRoute />,
                  children: [
                    {
                      path: ROUTES.supportChat,
                      element: <SupportChat />
                    }
                  ]
                }
              ]
            }
          ]
        },
        {
          path: '*',
          element: <NotFound />
        }
      ]
    }
  ],
  {
    patchRoutesOnNavigation: patchAdminRoutes
  }
);

const App: React.FC = () => {
  return <RouterProvider router={router} />;
};

export default App;
